import React from "react";

import {
    FastshiftRollUpStatus,
    FastshiftRelatedTicket,
    FASTSHIFT_RELATED_TICKET_ISSUE_TYPE
} from "@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";
import { FormattedMessage } from "react-intl";

import Badge from "@atlaskit/badge";
import { IconButton } from "@atlaskit/button-next/new";
import Heading from "@atlaskit/heading";
import CrossIcon from "@atlaskit/icon/core/cross";
import { Inline, xcss, Box, Stack } from "@atlaskit/primitives-next";
import { token } from "@atlaskit/tokens";

import { MigrationTicketsCard } from "@/common/migration-tickets-card";

import { useMigrationTicketsUIAnalytics } from "../hooks";
import { messages } from "../messages";

export type MigrationTicketsPanelProps = {
    supportTicketId: string;
    title: string;
    fastShiftStatusReason: FastshiftRollUpStatus;
    relatedTicketsCount: number;
    relatedTickets: FastshiftRelatedTicket[];
    onClose: () => void;
};

const cardWrapperStyles = xcss({
    border: `1px solid ${token("color.border")}`,
    borderRadius: "4px"
});

export const MigrationTicketsPanel: React.FC<MigrationTicketsPanelProps> = ({
    supportTicketId,
    title,
    fastShiftStatusReason,
    relatedTicketsCount,
    relatedTickets,
    onClose
}) => {
    const {
        sendRelatedTicketsSidePanelDismissButtonClickUIEvent,
        sendRelatedTicketsSidePanelOpenSupportTicketClickUIEvent
    } = useMigrationTicketsUIAnalytics();

    return (
        <Stack space="space.300" grow="fill">
            <Inline alignBlock="center" spread="space-between">
                <Heading size="medium">
                    <FormattedMessage
                        {...messages.migrationTicketsPanelTitle}
                    />
                </Heading>
                <IconButton
                    icon={CrossIcon}
                    label="Close"
                    onClick={() => {
                        onClose();
                        sendRelatedTicketsSidePanelDismissButtonClickUIEvent(
                            supportTicketId
                        );
                    }}
                    testId="close-button"
                />
            </Inline>
            <Box xcss={cardWrapperStyles}>
                <MigrationTicketsCard
                    compact={true}
                    issueType={FASTSHIFT_RELATED_TICKET_ISSUE_TYPE.PLAN}
                    supportTicketId={supportTicketId}
                    title={title}
                    status={fastShiftStatusReason}
                    onClickAnalyticsEvent={
                        sendRelatedTicketsSidePanelOpenSupportTicketClickUIEvent
                    }
                />
            </Box>
            {relatedTicketsCount > 0 && (
                <Stack space="space.100">
                    <Inline alignBlock="center" space="space.050">
                        <Heading size="xsmall">
                            <FormattedMessage
                                {...messages.migrationTicketsPanelRelatedTicketsTitle}
                            />
                        </Heading>
                        <Badge>{relatedTicketsCount}</Badge>
                    </Inline>
                    {relatedTickets.map((ticket) => (
                        <Box
                            xcss={cardWrapperStyles}
                            key={ticket.supportTicketId}
                        >
                            <MigrationTicketsCard
                                compact={true}
                                issueType={ticket.issueType}
                                supportTicketId={ticket.supportTicketId}
                                title={ticket.title}
                                status={ticket.fastShiftStatusReason}
                                onClickAnalyticsEvent={
                                    sendRelatedTicketsSidePanelOpenSupportTicketClickUIEvent
                                }
                            />
                        </Box>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};
